import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, CheckCircle, Clock, IndianRupee, Eye, Edit2, Trash2, Download,
  Send, ArrowRight, User, MoreVertical, XCircle,
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { proformaInvoicesAPI } from '../../services/part2-api';
import ColumnFilter from '../../components/shared/ColumnFilter';
import ListPageHeader from '../../components/shared/ListPageHeader';
import StaffFilterSelect from '../../components/shared/StaffFilterSelect';
import ConfirmModal from '../../components/modals/ConfirmModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getApiErrorMessage } from '../../utils/apiErrors';

const ProformaInvoicesPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [proformas, setProformas] = useState([]);
  const [confirmModal, setConfirmModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [stats, setStats] = useState({
    total: 0, draft: 0, sent: 0, accepted: 0, expired: 0, converted: 0,
    total_value: 0, pending_value: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Role-based visibility
  const [isOwner, setIsOwner] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState(null);

  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    proforma_number: '',
    customer_name: '',
    proforma_date: { min: '', max: '' },
    expiry_date: { min: '', max: '' },
    status: [],
    amount: { min: '', max: '' },
    staff_member: '',
  });

  const bid = businessId || activeBusiness?.id;
  const businessPath = bid ? `/business/${bid}` : '';

  useEffect(() => {
    if (bid) fetchProformas();
  }, [bid, statusFilter, selectedStaffId]);

  const fetchProformas = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter.toUpperCase();
      if (searchQuery) params.search = searchQuery;
      if (selectedStaffId) params.staff_id = selectedStaffId;

      const response = await proformaInvoicesAPI.list(params);
      const data = response.data;
      setProformas(data.proformas || []);
      setStats(data.stats || {});
      setIsOwner(data.is_owner !== false);
      setStaffList(data.staff_list || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load proforma invoices'));
    } finally {
      setLoading(false);
    }
  };

  // Apply all filters (search + column filters)
  // Party_name is the real column name post-migration; keep customer_name
  // as a fallback during the transition in case any cached responses
  // still carry the legacy name.
  const getPartyName = (p) => p.party_name || p.customer_name || '';

  const filteredProformas = proformas.filter((p) => {
    const matchesSearch = !searchQuery ||
      p.proforma_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getPartyName(p).toLowerCase().includes(searchQuery.toLowerCase());

    const matchesProformaNo = !columnFilters.proforma_number ||
      p.proforma_number?.toLowerCase().includes(columnFilters.proforma_number.toLowerCase());

    const matchesCustomer = !columnFilters.customer_name ||
      getPartyName(p).toLowerCase().includes(columnFilters.customer_name.toLowerCase());

    const matchesProformaDate = (!columnFilters.proforma_date?.min || new Date(p.proforma_date) >= new Date(columnFilters.proforma_date.min)) &&
      (!columnFilters.proforma_date?.max || new Date(p.proforma_date) <= new Date(columnFilters.proforma_date.max));

    const matchesExpiryDate = (!columnFilters.expiry_date?.min || new Date(p.expiry_date) >= new Date(columnFilters.expiry_date.min)) &&
      (!columnFilters.expiry_date?.max || new Date(p.expiry_date) <= new Date(columnFilters.expiry_date.max));

    const matchesStaff = !columnFilters.staff_member ||
      (p.created_by_staff_name || 'Owner').toLowerCase().includes(columnFilters.staff_member.toLowerCase());

    const matchesStatus = columnFilters.status.length === 0 ||
      columnFilters.status.includes(p.status);

    const matchesAmount = (!columnFilters.amount?.min || (p.grand_total || 0) >= parseFloat(columnFilters.amount.min)) &&
      (!columnFilters.amount?.max || (p.grand_total || 0) <= parseFloat(columnFilters.amount.max));

    return matchesSearch && matchesProformaNo && matchesCustomer && matchesProformaDate && matchesExpiryDate && matchesStatus && matchesAmount && matchesStaff;
  });

  const handleAddProforma = () => navigate(`${businessPath}/proforma-invoices/new`);
  const handleViewProforma = (p) => navigate(`${businessPath}/proforma-invoices/${p.id}`);
  const handleEditProforma = (p) => navigate(`${businessPath}/proforma-invoices/${p.id}/edit`);

  // Status transitions as ConfirmModals. Silent api.patch -> toast was
  // fine but gave no confirmation for stateful changes; inline a confirm
  // step so accidental clicks don't mark a live proforma as Sent/Accepted.
  const handleMarkSent = (proforma) => {
    setConfirmModal({
      title: 'Mark as Sent',
      description: `Mark ${proforma.proforma_number} as sent to the customer?`,
      confirmLabel: 'Mark as Sent',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: Send,
      iconClass: 'text-blue-600',
      iconBgClass: 'bg-blue-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await proformaInvoicesAPI.updateStatus(proforma.id, 'SENT');
          toast.success('Marked as Sent');
          fetchProformas();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to update status'));
        }
      },
    });
  };

  const handleMarkAccepted = (proforma) => {
    setConfirmModal({
      title: 'Mark as Accepted',
      description: `Record that the customer accepted ${proforma.proforma_number}?`,
      confirmLabel: 'Mark Accepted',
      confirmClass: 'bg-emerald-600 hover:bg-emerald-700',
      icon: CheckCircle,
      iconClass: 'text-emerald-600',
      iconBgClass: 'bg-emerald-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await proformaInvoicesAPI.updateStatus(proforma.id, 'ACCEPTED');
          toast.success('Marked as Accepted');
          fetchProformas();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to update status'));
        }
      },
    });
  };

  // Reject uses the ConfirmModal children slot to capture a reason,
  // persisted via the backend's ?reason= query param into
  // proforma_invoices.rejection_reason (real column since migration 2100).
  const handleReject = (proforma) => {
    setRejectReason('');
    setConfirmModal({
      title: 'Reject Proforma',
      description: 'Record the customer\u2019s reason for rejecting so your team has context later.',
      confirmLabel: 'Reject',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: XCircle,
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      kind: 'reject',
      onConfirm: async (reason) => {
        setConfirmModal(null);
        try {
          await proformaInvoicesAPI.updateStatus(proforma.id, 'REJECTED', reason || '');
          toast.success('Marked as Rejected');
          fetchProformas();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to reject proforma'));
        }
      },
    });
  };

  const handleConvertToInvoice = (proforma) => {
    setConfirmModal({
      title: 'Convert to Invoice',
      description: `Convert ${proforma.proforma_number} to a sales invoice?`,
      confirmLabel: 'Convert',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: ArrowRight,
      iconClass: 'text-blue-600',
      iconBgClass: 'bg-blue-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const response = await proformaInvoicesAPI.convertToInvoice(proforma.id);
          const data = response.data || {};
          const invoice = data.invoice || {};
          toast.success(`Converted to invoice ${invoice.invoice_number || ''}`);
          if (invoice.id) {
            navigate(`${businessPath}/sales/invoices/${invoice.id}`);
          } else {
            fetchProformas();
          }
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to convert to invoice'));
        }
      },
    });
  };

  const handleDeleteProforma = (proforma) => {
    setConfirmModal({
      title: 'Delete Proforma',
      description: `Delete ${proforma.proforma_number}? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconClass: 'text-red-600',
      iconBgClass: 'bg-red-100',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await proformaInvoicesAPI.delete(proforma.id);
          toast.success('Proforma deleted');
          fetchProformas();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to delete proforma'));
        }
      },
    });
  };

  const handleDownloadPdf = async (proforma) => {
    try {
      const response = await proformaInvoicesAPI.downloadPdf(proforma.id);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Proforma_${proforma.proforma_number || proforma.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to download PDF'));
    }
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft' },
      SENT: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Sent' },
      ACCEPTED: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Accepted' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
      EXPIRED: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Expired' },
      CONVERTED: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Converted' },
      CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' },
    };
    return badges[status] || badges.DRAFT;
  };

  const isExpired = (expiryDate) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isRejectFlow = confirmModal?.kind === 'reject';

  return (
    <>
    <div className="h-full flex flex-col" data-testid="proforma-invoices-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Proforma Invoices"
        stats={[
          { icon: FileText, label: 'Total', value: stats.total, color: 'slate' },
          { icon: Clock, label: 'Draft', value: stats.draft, color: 'amber' },
          { icon: Send, label: 'Sent', value: stats.sent, color: 'blue' },
          { icon: CheckCircle, label: 'Accepted', value: stats.accepted, color: 'emerald' },
          { icon: IndianRupee, label: 'Total Value', value: formatCompactCurrency(stats.total_value), color: 'emerald' },
        ]}
        ctaLabel="New Proforma Invoice"
        onCtaClick={handleAddProforma}
        extraControls={isOwner && staffList.length > 0 ? (
          <StaffFilterSelect
            staffList={staffList}
            selectedStaffId={selectedStaffId}
            onChange={setSelectedStaffId}
          />
        ) : null}
      />

      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading proformas...</p>
              </div>
            </div>
          ) : filteredProformas.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">{searchQuery ? 'No proformas found' : 'No proforma invoices yet'}</p>
              <p className="text-[13px] text-slate-400 mt-1 mb-4">{searchQuery ? 'Try adjusting your search' : 'Create your first proforma invoice'}</p>
              {!searchQuery && (
                <button onClick={handleAddProforma} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-[13px] font-medium">
                  Create Proforma Invoice
                </button>
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                      <th className="tl-th tl-th-teal" style={{ width: isOwner ? '12%' : '14%' }}>
                        <div className="flex items-center gap-1">
                          Proforma #
                          <ColumnFilter
                            type="text"
                            value={columnFilters.proforma_number}
                            onChange={(val) => setColumnFilters({ ...columnFilters, proforma_number: val })}
                            placeholder="Filter proforma..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: isOwner ? '18%' : '22%' }}>
                        <div className="flex items-center gap-1">
                          Customer
                          <ColumnFilter
                            type="text"
                            value={columnFilters.customer_name}
                            onChange={(val) => setColumnFilters({ ...columnFilters, customer_name: val })}
                            placeholder="Filter customer..."
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '11%' }}>
                        <div className="flex items-center gap-1">
                          Date
                          <ColumnFilter
                            type="date"
                            value={columnFilters.proforma_date}
                            onChange={(val) => setColumnFilters({ ...columnFilters, proforma_date: val })}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '11%' }}>
                        <div className="flex items-center gap-1">
                          Expiry
                          <ColumnFilter
                            type="date"
                            value={columnFilters.expiry_date}
                            onChange={(val) => setColumnFilters({ ...columnFilters, expiry_date: val })}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '10%' }}>
                        <div className="flex items-center gap-1">
                          Status
                          <ColumnFilter
                            type="status"
                            value={columnFilters.status}
                            onChange={(val) => setColumnFilters({ ...columnFilters, status: val })}
                            options={[
                              { value: 'DRAFT', label: 'Draft' },
                              { value: 'SENT', label: 'Sent' },
                              { value: 'ACCEPTED', label: 'Accepted' },
                              { value: 'REJECTED', label: 'Rejected' },
                              { value: 'EXPIRED', label: 'Expired' },
                              { value: 'CONVERTED', label: 'Converted' },
                            ]}
                          />
                        </div>
                      </th>
                      {isOwner && (
                        <th className="tl-th tl-th-teal" style={{ width: '13%' }}>
                          <div className="flex items-center gap-1">
                            <User className="w-3.5 h-3.5" />
                            Staff
                            <ColumnFilter
                              type="text"
                              value={columnFilters.staff_member}
                              onChange={(val) => setColumnFilters({ ...columnFilters, staff_member: val })}
                              placeholder="Filter staff..."
                            />
                          </div>
                        </th>
                      )}
                      <th className="tl-th tl-th-teal tl-th-right" style={{ width: '13%' }}>
                        <div className="flex items-center justify-end gap-1">
                          Amount
                          <ColumnFilter
                            type="number"
                            value={columnFilters.amount}
                            onChange={(val) => setColumnFilters({ ...columnFilters, amount: val })}
                          />
                        </div>
                      </th>
                      <th className="tl-th tl-th-teal" style={{ width: '60px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProformas.map((p, index) => {
                      const statusBadge = getStatusBadge(p.status);
                      const expired = isExpired(p.expiry_date) && !['CONVERTED', 'CANCELLED', 'EXPIRED'].includes(p.status);
                      const isDraft = p.status === 'DRAFT';
                      const isSent = p.status === 'SENT';
                      const isAccepted = p.status === 'ACCEPTED';
                      const canConvert = ['DRAFT', 'SENT', 'ACCEPTED'].includes(p.status);
                      const canDelete = ['DRAFT', 'CANCELLED'].includes(p.status);
                      return (
                        <tr
                          key={p.id}
                          className={`tl-tr tl-tr-clickable tl-tr-hover ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                          onClick={() => handleViewProforma(p)}
                        >
                          <td className="tl-td" style={{ width: isOwner ? '12%' : '14%' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-[12px] font-medium text-teal-700">
                                  {p.proforma_number?.slice(-2) || 'PI'}
                                </span>
                              </div>
                              <span className="tl-cell-primary text-teal-600 font-medium">{p.proforma_number}</span>
                            </div>
                          </td>
                          <td className="tl-td tl-cell-regular" style={{ width: isOwner ? '18%' : '22%' }}>{getPartyName(p) || '—'}</td>
                          <td className="tl-td tl-cell-regular" style={{ width: '11%' }}>
                            {p.proforma_date ? new Date(p.proforma_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </td>
                          <td className="tl-td" style={{ width: '11%' }}>
                            <span className={expired ? 'text-red-600 font-medium' : 'tl-cell-regular'}>
                              {p.expiry_date ? new Date(p.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                            </span>
                          </td>
                          <td className="tl-td" style={{ width: '10%' }}>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                              {statusBadge.label}
                            </span>
                          </td>
                          {isOwner && (
                            <td className="tl-td" style={{ width: '13%' }}>
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                  <User className="w-3 h-3 text-slate-500" />
                                </div>
                                <span className="text-[12px] text-slate-600 truncate">
                                  {p.created_by_staff_name || 'Owner'}
                                </span>
                              </div>
                            </td>
                          )}
                          <td className="tl-td tl-td-right tl-cell-regular" style={{ width: '13%' }}>
                            ₹{(p.grand_total || 0).toLocaleString('en-IN')}
                          </td>
                          <td className="tl-td" style={{ width: '60px' }} onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                                  title="Actions"
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem onClick={() => handleViewProforma(p)}>
                                  <Eye className="w-4 h-4 mr-2 text-slate-500" />
                                  View
                                </DropdownMenuItem>
                                {isDraft && (
                                  <DropdownMenuItem onClick={() => handleEditProforma(p)}>
                                    <Edit2 className="w-4 h-4 mr-2 text-slate-500" />
                                    Edit
                                  </DropdownMenuItem>
                                )}
                                {isDraft && (
                                  <DropdownMenuItem onClick={() => handleMarkSent(p)}>
                                    <Send className="w-4 h-4 mr-2 text-blue-600" />
                                    Mark as Sent
                                  </DropdownMenuItem>
                                )}
                                {isSent && (
                                  <DropdownMenuItem onClick={() => handleMarkAccepted(p)}>
                                    <CheckCircle className="w-4 h-4 mr-2 text-emerald-600" />
                                    Mark as Accepted
                                  </DropdownMenuItem>
                                )}
                                {isSent && (
                                  <DropdownMenuItem
                                    onClick={() => handleReject(p)}
                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                  >
                                    <XCircle className="w-4 h-4 mr-2" />
                                    Reject
                                  </DropdownMenuItem>
                                )}
                                {canConvert && (
                                  <DropdownMenuItem onClick={() => handleConvertToInvoice(p)}>
                                    <ArrowRight className="w-4 h-4 mr-2 text-blue-600" />
                                    Convert to Invoice
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleDownloadPdf(p)}>
                                  <Download className="w-4 h-4 mr-2 text-slate-500" />
                                  Download PDF
                                </DropdownMenuItem>
                                {canDelete && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => handleDeleteProforma(p)}
                                      className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>Showing <span className="font-medium text-slate-700">{filteredProformas.length}</span> proformas</p>
                <p>Total Value: <span className="font-medium text-slate-700">{formatCompactCurrency(stats.total_value || 0)}</span></p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    <ConfirmModal
      isOpen={!!confirmModal}
      title={confirmModal?.title}
      description={confirmModal?.description}
      confirmLabel={confirmModal?.confirmLabel}
      confirmClass={confirmModal?.confirmClass}
      icon={confirmModal?.icon}
      iconClass={confirmModal?.iconClass}
      iconBgClass={confirmModal?.iconBgClass}
      confirmDisabled={isRejectFlow && !rejectReason.trim()}
      onConfirm={() => confirmModal?.onConfirm?.(rejectReason)}
      onClose={() => { setConfirmModal(null); setRejectReason(''); }}
    >
      {isRejectFlow && (
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. Price too high, went with another vendor, timeline too tight..."
          rows={3}
          autoFocus
          className="w-full px-3 py-2 text-[13px] border border-slate-200 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 focus:outline-none resize-none"
        />
      )}
    </ConfirmModal>
    </>
  );
};

export default ProformaInvoicesPage;
